import React from "react";
import styled from "styled-components";
import { narrowScreenMediaQuery } from "../../constants/styles";
import { ImageWithSkeleton } from "../Skeleton";
import useReducedMotion from "../../hooks/useReducedMotion";

// Renders admin-managed banners: supports image and video.
// Backend-side: BannerV2 records supply { media_type: 'image'|'video', url, alt, link }.
// Images are lazy-loaded (first slide eagerly); a shimmer skeleton shows until
// each slide's media decodes.

const Wrap = styled.section`
  background: #fff;
  padding: 0;
  width: 100%;
  display: block;

  &[data-theme="dark"], [data-theme="dark"] & {
    background: #0f172a;
  }
`;

// Height follows the viewport width at the banner's native 1440:540 ratio
// (37.5vw). `clamp(...)` enforces a sensible floor (360px on narrow screens)
// and ceiling (720px on >1920px displays) so the hero never gets absurdly
// tall on 24"+ monitors. Viewport itself stays `width: 100%` so the banner
// always spans edge-to-edge — previous `aspect-ratio + max-height` combo
// caused some engines to shrink the Viewport's width on ultra-wide screens
// to preserve the ratio, leaving a blank gutter on the right.
const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: clamp(360px, 37.5vw, 720px);

  @media ${narrowScreenMediaQuery} {
    height: 66vw;
  }
`;

const Track = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1);
  transform: translateX(${({ $idx }) => `-${$idx * 100}%`});
`;

const Slide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  display: block;
  position: relative;
  background: #111;

  [data-theme="dark"] & {
    background: #020617;
  }
`;

// Hover treatment: a subtle darkening + a top-right "Kunjungi" pill that
// opens the banner's link in a new tab. Only rendered when the banner
// actually has a link (so banners that are purely decorative don't get a
// dead CTA). Also appears on focus-within for keyboard users.
const HoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  opacity: 0;
  pointer-events: none;
  transition: opacity 220ms ease;

  ${Slide}:hover &,
  ${Slide}:focus-within & {
    opacity: 1;
  }
`;

const VisitButton = styled.a`
  position: absolute;
  top: 20px;
  right: 20px;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.95);
  color: #111827;
  text-decoration: none;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 18px;
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  transform: translateY(-4px);
  opacity: 0;
  transition: opacity 220ms ease, transform 220ms ease, background 150ms ease;

  ${Slide}:hover &,
  ${Slide}:focus-within & {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    background: #d85907;
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
    opacity: 1;
    transform: translateY(0);
  }

  @media ${narrowScreenMediaQuery} {
    top: 12px;
    right: 12px;
    padding: 8px 14px;
    font-size: 12px;
  }

  [data-theme="dark"] & {
    background: rgba(23, 23, 30, 0.92);
    color: #f3f4f6;

    &:hover {
      background: #d85907;
      color: #fff;
    }
  }
`;

const VisitIcon = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;

  & svg { width: 100%; height: 100%; display: block; }
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => ($dir === "prev" ? "left: 20px;" : "right: 20px;")}
  background: rgba(255, 255, 255, 0.88);
  border: none;
  color: #1f2937;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 120ms ease, background 120ms ease;

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.05);
  }

  @media ${narrowScreenMediaQuery} {
    width: 36px;
    height: 36px;
  }

  &[data-theme="dark"], [data-theme="dark"] & {
    background: rgba(23, 23, 30, 0.85);
    color: #f3f4f6;

    &:hover {
      background: #17171e;
    }
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Dot = styled.button`
  border: none;
  width: ${({ $active }) => ($active ? "28px" : "10px")};
  height: 10px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? "#D85907" : "rgba(255,255,255,0.75)")};
  cursor: pointer;
  transition: all 180ms ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

// Hero banners come from Inertia props (serialized BannerV2 records with
// `status: :on`). When admins haven't published any banners — or the
// serializer returns an empty set — we render a brand-gradient placeholder
// with the same dimensions so the hero slot stays reserved (no CLS jump).
const EmptyHero = styled.div`
  width: 100%;
  height: clamp(360px, 37.5vw, 720px);
  background:
    radial-gradient(ellipse at 20% 20%, rgba(255,255,255,0.18), transparent 55%),
    linear-gradient(135deg, #1f2937 0%, #0f172a 100%);

  [data-theme="dark"] & {
    background:
      radial-gradient(ellipse at 20% 20%, rgba(255,255,255,0.08), transparent 55%),
      linear-gradient(135deg, #0f172a 0%, #020617 100%);
  }

  @media ${narrowScreenMediaQuery} {
    height: 66vw;
  }
`;

function HeroBanner({ banners: bannersFromProps }) {
  const banners = Array.isArray(bannersFromProps) ? bannersFromProps : [];
  const [idx, setIdx] = React.useState(0);
  const reducedMotion = useReducedMotion();

  React.useEffect(() => {
    if (banners.length <= 1) return;
    if (reducedMotion) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length, reducedMotion]);

  if (!banners.length) {
    // Reserves the hero slot instead of returning null, preventing CLS.
    return (
      <Wrap aria-label="Banner">
        <EmptyHero role="presentation" />
      </Wrap>
    );
  }

  const go = (i) => setIdx(((i % banners.length) + banners.length) % banners.length);

  return (
    <Wrap aria-label="Banner">
      <Viewport>
        <Track $idx={idx}>
          {banners.map((b, i) => {
            const hasLink = !!(b.link && b.link !== '/' && b.link !== '#');
            return (
              <Slide key={b.id} aria-label={b.alt || "Banner"}>
                <ImageWithSkeleton
                  src={b.url}
                  alt={b.alt || ""}
                  mediaType={b.media_type}
                  lazy={i !== 0}
                />
                {hasLink && (
                  <HoverOverlay aria-hidden="true">
                    <VisitButton
                      href={b.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={b.alt ? `Kunjungi: ${b.alt}` : "Kunjungi tautan banner"}
                    >
                      <span>Kunjungi</span>
                      <VisitIcon aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 17L17 7" />
                          <path d="M9 7h8v8" />
                        </svg>
                      </VisitIcon>
                    </VisitButton>
                  </HoverOverlay>
                )}
              </Slide>
            );
          })}
        </Track>
        {banners.length > 1 && (
          <>
            <NavBtn $dir="prev" aria-label="Sebelumnya" onClick={() => go(idx - 1)}>‹</NavBtn>
            <NavBtn $dir="next" aria-label="Selanjutnya" onClick={() => go(idx + 1)}>›</NavBtn>
            <Dots role="tablist" aria-label="Slide indicator">
              {banners.map((_, i) => (
                <Dot
                  key={i}
                  $active={i === idx}
                  aria-label={`Slide ${i + 1}`}
                  aria-selected={i === idx}
                  onClick={() => go(i)}
                />
              ))}
            </Dots>
          </>
        )}
      </Viewport>
    </Wrap>
  );
}

export default React.memo(HeroBanner);
